import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'

import { reatomActivityState } from './atoms/reatorm-activity-state'

import type { TypeHelper } from '@blade/std-lib'
import type { ButtonProps } from '@blade/ui-kit'
import type { ActivityType } from '@root/entities/activity-action-type'
import type { DelugeId } from '@root/entities/user-short'
import type { TransKey } from '@root/shared/localization'

const nextActionName = {
  following: 'common.unfollow',
  'not-following': 'common.follow',
} as const satisfies Record<ActivityType.ActivityType, TransKey>

type FollowUserButtonProps = {
  delugeId: DelugeId
} & TypeHelper.OmitKey<ButtonProps, 'children' | 'onClick'>

export const FollowUserButton = ({
  delugeId,
  ...buttonProps
}: FollowUserButtonProps) => {
  const { t } = useTranslation()

  const {
    followingStateAtom,
    isFollowingAtom,
    changeActivityStateAction,
    refreshActivityStateAction,
  } = useMemo(() => reatomActivityState({ delugeId }), [delugeId])
  const [followingState] = useAtom(followingStateAtom)
  const [isFollowing] = useAtom(isFollowingAtom)
  const changeActivityState = useAction(changeActivityStateAction)
  const refreshActivityState = useAction(refreshActivityStateAction)

  return AsyncStateN.matchWaiting(followingState, {
    Error: () =>
      isFollowing.value ? (
        <Button onClick={() => refreshActivityState()} {...buttonProps}>
          {t(nextActionName[isFollowing.value])}
        </Button>
      ) : undefined,
    Waiting: () => <Button {...buttonProps} busy disabled />,
    Success: (activityType) =>
      activityType ? (
        <Button onClick={() => changeActivityState()} {...buttonProps}>
          {followingState.value
            ? t(nextActionName[followingState.value])
            : undefined}
        </Button>
      ) : undefined,
  })
}
